import React, { UIEventHandler, useRef } from 'react'
import { Form, Select as SelectAnt } from 'antd'
import { TooltipLabelComponent, i18n } from 'LibNeo'
import { GetFieldDecoratorOptions, WrappedFormUtils } from 'antd/lib/form/Form'
import { FormItemProps } from 'antd/lib/form'
import { BaseItem, SelectItem } from 'Types/item'
import {
	ErrorText,
	PrependLine,
	SelectWrapper,
	StyledAntdSelect,
	StyledSelectAntdOption,
} from './styled'
import { ClearIcon, RemoveIcon, SuffixIcon } from './Icons'

export type SelectGroup<T extends SelectItem> = {
	name: string
	items: T[]
}

export type FieldKeyOrLabel<T extends BaseItem> = keyof T

export type SelectValue<T extends SelectItem> = T[FieldKeyOrLabel<T>] | T[FieldKeyOrLabel<T>][]

export interface SelectComponentProps<T extends SelectItem = BaseItem> {
	form?: WrappedFormUtils
	items?: T[]
	fieldName?: string
	mode?: 'default' | 'multiple' | 'tags'
	dataQa?: string
	disabled?: boolean
	label?: string
	placeholder?: string
	className?: string
	fieldKey?: FieldKeyOrLabel<T>
	fieldLabel?: FieldKeyOrLabel<T>
	fieldOptions?: GetFieldDecoratorOptions
	optionsItem?: FormItemProps
	filterOption?: boolean
	onChange?: (value: SelectValue<T>) => void
	showSearch?: boolean
	allowClear?: boolean
	showArrow?: boolean
	onSearch?: (value: string) => void
	onFocus?: () => void
	onPopupScroll?: UIEventHandler<HTMLElement>
	value?: SelectValue<T>
	defaultValue?: SelectValue<T>
	errorText?: string
	getGroups?: (items: T[]) => SelectGroup<T>[]
	renderTitleGroup?: (item: SelectGroup<T>) => JSX.Element
	renderButtonItem?: (item: T) => JSX.Element
	tooltip?: string
	link?: string
	href?: string
	linkAction?: () => void
	notFoundContent?: string
	dropdownWidth?: number
	requiredHighlight?: boolean
}

export const DEFAULT_FIELD_KEY = 'id'
export const DEFAULT_FIELD_LABEL = 'name'

export function SelectComponent<T extends SelectItem = SelectItem>({
	form,
	items,
	className,
	placeholder,
	label,
	disabled = false, // гарантирует появление атрибута data-qa-disabled, типа boolean | undef
	dataQa,
	fieldName,
	filterOption = true,
	mode = 'default',
	showSearch = true,
	allowClear,
	onSearch,
	onPopupScroll,
	fieldKey = DEFAULT_FIELD_KEY,
	fieldLabel = DEFAULT_FIELD_LABEL,
	defaultValue,
	value,
	fieldOptions,
	optionsItem,
	tooltip,
	getGroups,
	renderTitleGroup,
	renderButtonItem,
	onChange,
	onFocus,
	showArrow = true,
	link,
	href,
	linkAction,
	notFoundContent = 'Ничего не найдено',
	errorText,
	dropdownWidth,
	requiredHighlight,
}: SelectComponentProps<T>) {
	const onChangeValue = (value: SelectValue<T>) => {
		if (getGroups) {
			onChange?.(items.find((curr) => curr[fieldKey] === value)[fieldKey])
		} else onChange?.(value)
	}

	const onSearchValue = (v: string) => {
		onSearch?.(v)
	}

	const selectWrapper = useRef<HTMLDivElement>(null)

	const hasError = Boolean(errorText || form?.getFieldError(fieldName))
	const isRequired = fieldOptions?.rules?.some((rule) => rule.required)
	const getOptions = (items: T[]) => {
		return items?.map((item) => {
			const key = item[fieldKey] as unknown as number | string
			return (
				<StyledSelectAntdOption key={key} value={key} disabled={item.disabled}>
					{item[fieldLabel]}
				</StyledSelectAntdOption>
			)
		})
	}

	const getGroupOptions = (items: T[]) => {
		const groups = getGroups(items)
		return groups.map((group) => {
			const { name, items: itemsInGroup } = group
			return (
				<SelectAnt.OptGroup label={renderTitleGroup ? renderTitleGroup(group) : name} key={name}>
					{itemsInGroup.map((item) => {
						const key = item[fieldKey] as unknown as number | string
						return (
							<StyledSelectAntdOption key={key} value={key} disabled={item.disabled}>
								<div>
									<div>{item[fieldLabel]}</div>
									{renderButtonItem?.(item)}
								</div>
							</StyledSelectAntdOption>
						)
					})}
				</SelectAnt.OptGroup>
			)
		})
	}

	const getValueProps = () => {
		if (form) return {}
		return value ? { value } : { defaultValue }
	}

	const select = (
		<StyledAntdSelect
			data-qa={dataQa}
			disabled={disabled}
			data-qa-disabled={disabled}
			className={className}
			placeholder={i18n(placeholder || 'common.no_select')}
			mode={mode}
			notFoundContent={notFoundContent}
			showSearch={showSearch}
			allowClear={allowClear}
			onPopupScroll={onPopupScroll}
			onChange={onChangeValue}
			onSearch={onSearchValue}
			onBlur={() => onSearchValue(null)}
			onFocus={onFocus}
			getPopupContainer={() => selectWrapper.current}
			showArrow={showArrow}
			clearIcon={ClearIcon}
			removeIcon={RemoveIcon}
			suffixIcon={SuffixIcon}
			filterOption={form ? filterOption : false}
			optionFilterProp="children"
			hasError={hasError}
			requiredHighlight={requiredHighlight}
			defaultActiveFirstOption={false}
			{...getValueProps()}
		>
			{getGroups ? getGroupOptions(items) : getOptions(items)}
		</StyledAntdSelect>
	)

	return (
		<SelectWrapper ref={selectWrapper} width={dropdownWidth}>
			{(label || tooltip) && (
				<PrependLine>
					<TooltipLabelComponent
						title={i18n(label)}
						required={isRequired}
						tooltip={tooltip}
						link={link}
						href={href}
						linkAction={linkAction}
					/>
				</PrependLine>
			)}
			{form ? (
				<Form.Item {...optionsItem}>
					{form.getFieldDecorator(fieldName, fieldOptions)(select)}
				</Form.Item>
			) : (
				select
			)}
			<ErrorText hasError={hasError}>
				<span>{errorText}</span>
			</ErrorText>
		</SelectWrapper>
	)
}
